import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Button, Card, Dialog, ErrorBanner, FilterSelect, Input, PageHeader, Pagination, SearchBox, Select, Spinner, Table, Textarea } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime, humanize, inr } from '../../lib/format';
import { useAction, useUrlState, type Paged } from '../../lib/hooks';

interface Row { variantId: string; sku: string; label: string; productId: string; productName: string; onHand: number; reserved: number; available: number; lowStockThreshold: number; valueAtPricePaise: number; status: string }
interface Movement { id: string; type: string; quantity: number; onHandAfter: number; reservedAfter: number; reason: string | null; createdAt: string }
const DEFAULTS = { q: '', filter: 'all', page: '1' };

export function Inventory() {
  const { can } = useAuth();
  const [f, setF] = useUrlState(DEFAULTS);
  const query = { ...f, pageSize: 50 };
  const q = useQuery({ queryKey: ['inventory', query], queryFn: () => api<Paged<Row>>('/admin/inventory', { query }), placeholderData: keepPreviousData });
  const [adjust, setAdjust] = useState<Row | null>(null);
  const [history, setHistory] = useState<Row | null>(null);
  return (
    <>
      <PageHeader title="Inventory" subtitle="Available = on hand − reserved by unpaid orders." />
      <Card>
        <div className="mb-3 flex flex-wrap gap-2">
          <SearchBox value={f.q} onChange={(v) => setF({ q: v })} placeholder="Product or SKU" />
          <FilterSelect label="Stock level" value={f.filter} onChange={(v) => setF({ filter: v })} options={[{ value: 'all', label: 'All stock' }, { value: 'low', label: 'Low stock' }, { value: 'out', label: 'Out of stock' }]} />
        </div>
        {q.isLoading ? <Spinner /> : q.error ? <ErrorBanner error={q.error} /> : (
          <>
            <Table rows={q.data!.items} rowKey={(r) => r.variantId} empty="Nothing here." columns={[
              { header: 'Product', cell: (r) => <div><div className="font-medium">{r.productName} — {r.label}</div><div className="text-xs text-ink-muted">{r.sku}</div></div> },
              { header: 'On hand', cell: (r) => r.onHand, className: 'text-right' },
              { header: 'Reserved', cell: (r) => r.reserved, className: 'text-right' },
              { header: 'Available', cell: (r) => <strong>{r.available}</strong>, className: 'text-right' },
              { header: 'Alert at', cell: (r) => r.lowStockThreshold, className: 'text-right' },
              { header: 'Value', cell: (r) => inr(r.valueAtPricePaise), className: 'text-right' },
              { header: 'Status', cell: (r) => <Badge status={r.status} /> },
              { header: '', cell: (r) => <div className="flex gap-1">{can('inventory.manage') && <Button variant="secondary" onClick={() => setAdjust(r)}>Adjust</Button>}<Button variant="ghost" onClick={() => setHistory(r)}>History</Button></div> },
            ]} />
            <Pagination page={q.data!.page} totalPages={q.data!.totalPages} total={q.data!.total} onPage={(p) => setF({ page: String(p) } as never)} />
          </>
        )}
      </Card>
      {adjust && <AdjustDialog row={adjust} onClose={() => setAdjust(null)} />}
      {history && <HistoryDialog row={history} onClose={() => setHistory(null)} />}
    </>
  );
}

function AdjustDialog({ row, onClose }: { row: Row; onClose: () => void }) {
  const [type, setType] = useState<'RECEIPT' | 'DAMAGE' | 'ADJUSTMENT'>('RECEIPT');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [threshold, setThreshold] = useState(String(row.lowStockThreshold));
  const n = Number(qty);
  const valid = Number.isInteger(n) && n !== 0 && (type === 'ADJUSTMENT' || n > 0) && reason.trim().length >= 3;
  const after = row.onHand + (type === 'DAMAGE' ? -Math.abs(n) : n || 0);
  const m = useAction((_: void, key: string) => api(`/admin/inventory/${row.variantId}/adjustments`, { method: 'POST', idempotencyKey: key, body: { type, quantity: n, reason: reason.trim() } }), { success: 'Stock updated', invalidate: [['inventory'], ['products'], ['dashboard']], onSuccess: onClose });
  const t = useAction(() => api(`/admin/inventory/${row.variantId}`, { method: 'PATCH', body: { lowStockThreshold: Number(threshold) } }), { success: 'Alert level saved', invalidate: [['inventory']] });
  return (
    <Dialog open title={`${row.productName} — ${row.label}`} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={m.isPending} disabled={!valid} onClick={() => m.mutate()}>Save stock change</Button></>}>
      <p className="text-sm">On hand <strong>{row.onHand}</strong> · reserved {row.reserved} · available {row.available}</p>
      <Select label="Type" value={type} onChange={(e) => setType(e.target.value as typeof type)} options={[{ value: 'RECEIPT', label: 'Goods received (+)' }, { value: 'DAMAGE', label: 'Damaged / expired (−)' }, { value: 'ADJUSTMENT', label: 'Stock-take correction (±)' }]} />
      <Input label={type === 'ADJUSTMENT' ? 'Change (use − to reduce)' : 'Quantity'} type="number" step={1} value={qty} onChange={(e) => setQty(e.target.value)} hint={qty && Number.isInteger(n) ? `On hand after: ${after}` : undefined} />
      <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} hint="e.g. Batch B-1027 from dairy; 2 jars cracked in transit" />
      <div className="flex items-end gap-2 border-t border-cream-border pt-3"><Input label="Low-stock alert at" type="number" min={0} value={threshold} onChange={(e) => setThreshold(e.target.value)} /><Button variant="secondary" loading={t.isPending} onClick={() => t.mutate(undefined)}>Save alert</Button></div>
    </Dialog>
  );
}

function HistoryDialog({ row, onClose }: { row: Row; onClose: () => void }) {
  const q = useQuery({ queryKey: ['movements', row.variantId], queryFn: () => api<Paged<Movement>>(`/admin/inventory/${row.variantId}/movements`, { query: { pageSize: 100 } }) });
  return (
    <Dialog open wide title={`Stock history — ${row.sku}`} onClose={onClose}>
      {q.isLoading ? <Spinner /> : <Table rows={q.data?.items ?? []} rowKey={(m) => m.id} columns={[
        { header: 'When', cell: (m) => dateTime(m.createdAt) },
        { header: 'Type', cell: (m) => humanize(m.type) },
        { header: 'Change', cell: (m) => (m.quantity > 0 ? `+${m.quantity}` : m.quantity), className: 'text-right' },
        { header: 'On hand after', cell: (m) => m.onHandAfter, className: 'text-right' },
        { header: 'Reason', cell: (m) => m.reason ?? '—' },
      ]} />}
    </Dialog>
  );
}
